// @flow

import { v4 } from 'uuid';
import React, { useMemo } from 'react';
import type { Node } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSelector, shallowEqual } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Section, Wrapper } from '@riseart/layout';
import { MediaQuery, VerticalMenu, Logo, Link } from '@riseart/common';
import { ProfileDetailCard } from '@riseart/artist';
import { gridEvenColumnsVisibleItemCls } from '@riseart/grid';
import { nl2br } from '@riseart/fe-utils';
import { HTTP_STATUS } from 'Enum';
import { useQuery } from 'shared_services/apollo/useQuery';
import { LayoutDefault } from 'shared_components/layouts/Default';
import { ArtistBreadcrumbs } from 'shared_components/artist/breadcrumbs/Breadcrumbs';
import { ArtistHeader } from 'shared_components/artist/header/Header';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import { VerticalMenuOverlay } from 'shared_components/common/menu/overlay/Menu';
import { ArtistDetailMeta } from 'shared_components/artist/detail/Meta';
import { ArtistCv as ArtistCvSection } from 'shared_components/artist/Cv';
import { ArtistBirthText } from 'shared_components/artist/bio/BirthText';
import { ArtistLivesText } from 'shared_components/artist/bio/LivesText';
import { DataProviderPreloaderPage } from 'shared_components/data/providers/preloaders/Page';
import { useUrlParams } from 'shared_data/providers/url/useUrlParams';
import { localeSelector } from 'shared_services/redux/selectors/locale';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { gql404RedirectHandler } from 'shared_services/riseart/utils/RouteUtils';
import { FollowButton } from 'shared_components/common/follow/Button';
import { FilterFollowsProvider } from 'shared_data/providers/follow/Follow';
import { ArtistShare } from 'shared_components/artist/share/Share';
import { CreateFollowProvider } from 'shared_data/providers/follow/CreateFollow';
import { MENU_ITEMS, mapArtistPageData } from 'shared_services/riseart/utils/artist';
import DETAIL_ARTIST from 'shared_data/queries/artist/detail.graphql';

import { raScreenLg } from '@riseart/antd-provider/dist/website/variables.less';
import { artistHeaderSectionCls } from 'shared_components/artist/header/Header.less';
import {
  artistCvCls,
  artistCvPrintHeaderCls,
  artistCvPrintLinkCls,
} from 'shared_components/pages/artist/Cv.less';

type Props = {
  staticContext: ?Object,
};

const PRELOADER_SUBSCRIBER_IDS = { readArtist: v4() };

/**
 * ArtistCvPage
 *
 * @param {Props} props
 * @returns {Node}
 */
export default function ArtistCvPage({ staticContext }: Props): Node {
  const { id: artistId } = useParams();
  const history = useHistory();
  const { translatedLocation: location } = useUrlParams();
  const currentLocale = useSelector(localeSelector, shallowEqual);
  const { formatMessage } = useIntl();
  const { loading, data, error } = useQuery(DETAIL_ARTIST, {
    variables: { id: artistId },
    context: {
      customOptions: {
        errorSuppressFromResponse: true,
        errorHandler: gql404RedirectHandler(
          UrlAssembler.artistList({
            search: { message: HTTP_STATUS.NOT_FOUND },
            locale: currentLocale,
          }),
          history,
          staticContext,
        ),
      },
    },
  });
  const { detailArtist: artistData } = data || {};
  const {
    hasError,
    canonicalURL,
    subpageCanonicalRedirect,
    hrefLangRedirects,
    artistUrl,
    name,
    artistMenuItems,
    artistImages: { mainBannerImage },
  } = useMemo(
    () =>
      mapArtistPageData(artistData, error, MENU_ITEMS.cv, 'artistCv', {
        formatMessage,
        locale: currentLocale,
        location,
      }),
    [artistData, error, formatMessage, currentLocale, location],
  );

  return (
    <LayoutDefault scrollTopOnPathChange className={artistCvCls}>
      <DataProviderPreloaderPage uniqueId={PRELOADER_SUBSCRIBER_IDS.readArtist} attach={loading} />
      {loading && <IsomorphicRipple isActive isFullScreen />}
      {artistData ? (
        <React.Fragment>
          {/* Meta Provider */}
          <ArtistDetailMeta
            hasError={hasError}
            artistData={artistData}
            mainBannerImage={mainBannerImage}
            canonicalURL={canonicalURL}
            subpageCanonicalRedirect={subpageCanonicalRedirect}
            hrefLangRedirects={hrefLangRedirects}
            formatMessage={formatMessage}
            translationKeyPrefix="meta.artistCv"
            translationMetas={{ metaTitle: true }}
            noIndex
          />
          <Section className={artistHeaderSectionCls}>
            <Wrapper>
              <ArtistBreadcrumbs
                pageLabel={MENU_ITEMS.cv}
                id={artistData.id}
                alias={artistData.alias}
                name={name}
                mediums={artistData.mediums}
                styles={artistData.styles}
              />
            </Wrapper>
            <div className={artistCvPrintHeaderCls}>
              <Logo tag="div" theme="black" />
            </div>
            <FormattedMessage id="components.artist.cvHeader">
              {(title: string) => (
                <ArtistHeader
                  title={title}
                  subtitle={
                    artistUrl ? (
                      <Link to={artistUrl} title={name}>
                        {name}
                      </Link>
                    ) : (
                      name
                    )
                  }
                >
                  <div className={artistCvPrintLinkCls}>
                    <FormattedMessage id="components.artist.viewCv">
                      {(text: string) => (
                        <Link href={canonicalURL} title={text} external type="light">
                          {canonicalURL}
                        </Link>
                      )}
                    </FormattedMessage>
                  </div>
                  <FilterFollowsProvider>
                    <CreateFollowProvider leadersList={artistData} />
                    <FollowButton leaderId={artistData.id} leaderData={artistData} />
                  </FilterFollowsProvider>
                  <ArtistShare name={name} image={mainBannerImage} url={canonicalURL} />
                  <MediaQuery minWidth={raScreenLg}>
                    {(isScreenLg) =>
                      isScreenLg ? (
                        <VerticalMenu disableFixedMenu absolutePosition items={artistMenuItems} />
                      ) : (
                        <VerticalMenuOverlay title={name} items={artistMenuItems} />
                      )
                    }
                  </MediaQuery>
                </ArtistHeader>
              )}
            </FormattedMessage>
          </Section>
        </React.Fragment>
      ) : null}
      <ArtistCvSection artistId={artistId} loading={loading}>
        {artistData ? (
          <div className={gridEvenColumnsVisibleItemCls}>
            <ProfileDetailCard
              title={name}
              born={
                artistData.birthCountry || artistData.birthYear || artistData.birthCity ? (
                  <ArtistBirthText
                    birthCountry={artistData.birthCountry}
                    birthCity={artistData.birthCity}
                    birthYear={artistData.birthYear}
                  />
                ) : null
              }
              lives={
                artistData.livesCountry || artistData.livesCity ? (
                  <ArtistLivesText
                    livesCountry={artistData.livesCountry}
                    livesCity={artistData.livesCity}
                  />
                ) : null
              }
              bio={
                artistData.statement ? (
                  <span dangerouslySetInnerHTML={{ __html: nl2br(artistData.statement) }} />
                ) : null
              }
              translations={{
                born: (
                  <FormattedMessage id="components.artist.bornInLabel">
                    {(label: string) => `${label}:`}
                  </FormattedMessage>
                ),
                lives: (
                  <FormattedMessage id="components.artist.livesLabel">
                    {(label: string) => `${label}:`}
                  </FormattedMessage>
                ),
              }}
            />
          </div>
        ) : null}
      </ArtistCvSection>
    </LayoutDefault>
  );
}
